import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { PencilSquare, PlusLg, Search } from 'react-bootstrap-icons';
import {
  addTrackingDetail,
  createOrderTracking,
  getOrderTracking,
  searchOrderTracking,
  updateOrderTracking,
  updateTrackingDetail
} from '../../services/api/orderTrackingService';
import './OrderTrackingPage.css';

const emptyHeader = {
  SALES_ORDER_NO: '',
  CUSTOMER_ORDER_REFERENCE: '',
  ENTITY: 'OBSS',
  PLANNED_DELIVERY_DATE: '',
  CURRENT_DELIVERY_DATE: ''
};
const emptyDetail = { MODE_OF_SHIPMENT: '', TRACKING_DETAILS: '', DELIVERY_STATUS: 'Pending' };
const entities = ['OBSS', 'SmartAuto'];
const statuses = ['Pending', 'In Transit', 'Delivered'];
const shipmentModes = ['Truck', 'Air', 'courier', 'Transport'];

const dateInputValue = (value) => value ? value.slice(0, 10) : '';
const displayDate = (value) => value
  ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    })
  : '—';

function OrderTrackingPage() {
  const [filters, setFilters] = useState({ salesOrderNo: '', entity: '', overdue: false });
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [header, setHeader] = useState(emptyHeader);
  const [detail, setDetail] = useState(emptyDetail);
  const [editingHeader, setEditingHeader] = useState(false);
  const [editingDetailId, setEditingDetailId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const showMessage = (type, text) => setMessage({ type, text });
  const apiError = (error) => {
    const data = error.response?.data;
    if (typeof data === 'string') return data;
    return data?.Message || 'The request could not be completed. Please try again.';
  };
  const overdueCount = orders.filter((order) => order.IS_OVERDUE).length;

  const runSearch = async (nextFilters = filters) => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await searchOrderTracking(nextFilters);
      setOrders(response.data || []);
    } catch (error) {
      showMessage('error', apiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runSearch(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectOrder = async (id) => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await getOrderTracking(id);
      setSelected(response.data);
      setEditingHeader(false);
      setEditingDetailId(null);
      setDetail({ ...emptyDetail, DELIVERY_STATUS: response.data.DELIVERY_STATUS || 'Pending' });
    } catch (error) {
      showMessage('error', apiError(error));
    } finally {
      setLoading(false);
    }
  };

  const startCreate = () => {
    setSelected(null);
    setHeader(emptyHeader);
    setEditingHeader(true);
    setEditingDetailId(null);
    setMessage(null);
  };

  const startHeaderEdit = () => {
    setHeader({
      SALES_ORDER_NO: selected.SALES_ORDER_NO,
      CUSTOMER_ORDER_REFERENCE: selected.CUSTOMER_ORDER_REFERENCE || '',
      ENTITY: selected.ENTITY,
      PLANNED_DELIVERY_DATE: dateInputValue(selected.PLANNED_DELIVERY_DATE),
      CURRENT_DELIVERY_DATE: dateInputValue(selected.CURRENT_DELIVERY_DATE)
    });
    setEditingHeader(true);
    setMessage(null);
  };

  const saveHeader = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const response = selected
        ? await updateOrderTracking(selected.ID, header)
        : await createOrderTracking(header);
      setSelected(response.data);
      setEditingHeader(false);
      const successMessage = selected ? 'Order tracking updated.' : 'Order tracking created.';
      await runSearch();
      showMessage('success', successMessage);
    } catch (error) {
      showMessage('error', apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const startDetailEdit = (item) => {
    setEditingDetailId(item.ID);
    setDetail({
      MODE_OF_SHIPMENT: item.MODE_OF_SHIPMENT || '',
      TRACKING_DETAILS: item.TRACKING_DETAILS,
      DELIVERY_STATUS: selected.DELIVERY_STATUS || 'Pending'
    });
    setMessage(null);
  };

  const saveDetail = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      if (editingDetailId) {
        await updateTrackingDetail(selected.ID, editingDetailId, detail);
      } else {
        await addTrackingDetail(selected.ID, detail);
      }
      const response = await getOrderTracking(selected.ID);
      setSelected(response.data);
      setDetail({ ...emptyDetail, DELIVERY_STATUS: response.data.DELIVERY_STATUS || 'Pending' });
      setEditingDetailId(null);
      await runSearch();
      showMessage('success', editingDetailId ? 'Tracking entry updated.' : 'Tracking entry added.');
    } catch (error) {
      showMessage('error', apiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="tracking-page">
      <div className="card aon-card tracking-card">
        <div className="card-header aon-card-header tracking-card-header">
          <h4>Order Tracking</h4>
          <button className="admin-button tracking-admin-button" type="button" onClick={startCreate}>
            <PlusLg aria-hidden="true" /> New order
          </button>
        </div>

        <div className="card-body aon-card-body tracking-card-body">
          {message && (
            <div className={`tracking-message ${message.type}`} role="status" aria-live="polite">
              {message.text}
            </div>
          )}

      <section className="tracking-search" aria-label="Search orders">
        <Form.Group>
          <Form.Label htmlFor="order-search">Sales Order Number</Form.Label>
          <Form.Control
            id="order-search"
            value={filters.salesOrderNo}
            placeholder="Search by order number"
            onChange={(e) => setFilters({ ...filters, salesOrderNo: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="entity-filter">Entity</Form.Label>
          <Form.Select
            id="entity-filter"
            value={filters.entity}
            onChange={(e) => setFilters({ ...filters, entity: e.target.value })}
          >
            <option value="">All entities</option>
            {entities.map((item) => <option key={item}>{item}</option>)}
          </Form.Select>
        </Form.Group>
        <Button variant="outline-primary" onClick={() => runSearch()} disabled={loading}>
          <Search aria-hidden="true" /> Search
        </Button>
      </section>

          <div className="tracking-workspace">
        <section className="tracking-list" aria-label="Order search results">
          <div className="tracking-section-heading">
            <h2>Orders</h2>
            <span>{orders.length} found · {overdueCount} overdue</span>
          </div>
          <div className="tracking-list-filters" aria-label="Search orders">
            <Form.Group>
              <Form.Label htmlFor="order-search-list">Sales Order Number</Form.Label>
              <Form.Control
                id="order-search-list"
                value={filters.salesOrderNo}
                placeholder="Search order"
                onChange={(e) => setFilters({ ...filters, salesOrderNo: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label htmlFor="entity-filter-list">Entity</Form.Label>
              <Form.Select
                id="entity-filter-list"
                value={filters.entity}
                onChange={(e) => setFilters({ ...filters, entity: e.target.value })}
              >
                <option value="">All entities</option>
                {entities.map((item) => <option key={item}>{item}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tracking-overdue-filter">
              <Form.Check
                id="overdue-filter-list"
                type="checkbox"
                label="Overdue only"
                checked={Boolean(filters.overdue)}
                onChange={(e) => setFilters({ ...filters, overdue: e.target.checked })}
              />
            </Form.Group>
            <button className="admin-button tracking-search-button" type="button" onClick={() => runSearch()} disabled={loading}>
              <Search aria-hidden="true" /> Search
            </button>
          </div>
          {loading && !selected ? (
            <div className="tracking-empty"><Spinner size="sm" /> Loading orders…</div>
          ) : orders.length === 0 ? (
            <div className="tracking-empty">No orders match the current filters.</div>
          ) : orders.map((order) => (
            <button
              type="button"
              key={order.ID}
              className={`tracking-order ${selected?.ID === order.ID ? 'active' : ''} ${order.IS_OVERDUE ? 'overdue' : ''}`}
              onClick={() => selectOrder(order.ID)}
            >
              <span>
                <strong>{order.SALES_ORDER_NO}</strong>
                <small>{order.ENTITY}</small>
              </span>
              {order.IS_OVERDUE && <span className="tracking-overdue-badge">Overdue</span>}
              <span className={`tracking-status ${order.DELIVERY_STATUS.toLowerCase().replace(/\s+/g, '-')}`}>
                {order.DELIVERY_STATUS}
              </span>
              <small>Current: {displayDate(order.CURRENT_DELIVERY_DATE)}</small>
            </button>
          ))}
        </section>

        <section className="tracking-detail">
          {editingHeader ? (
            <HeaderForm
              value={header}
              setValue={setHeader}
              isNew={!selected}
              saving={saving}
              onSubmit={saveHeader}
              onCancel={() => setEditingHeader(false)}
            />
          ) : selected ? (
            <>
              <div className="tracking-section-heading detail-heading">
                <div>
                  <p className="tracking-eyebrow">{selected.ENTITY}</p>
                  <h2>{selected.SALES_ORDER_NO}</h2>
                </div>
                <Button variant="outline-secondary" onClick={startHeaderEdit}>
                  <PencilSquare aria-hidden="true" /> Edit order
                </Button>
              </div>
              <dl className="tracking-summary">
                <div><dt>Customer order ref</dt><dd>{selected.CUSTOMER_ORDER_REFERENCE || '-'}</dd></div>
                <div><dt>Status</dt><dd>{selected.DELIVERY_STATUS}</dd></div>
                <div><dt>Planned delivery</dt><dd>{displayDate(selected.PLANNED_DELIVERY_DATE)}</dd></div>
                <div><dt>Current delivery</dt><dd>{displayDate(selected.CURRENT_DELIVERY_DATE)}</dd></div>
              </dl>

              <div className="tracking-timeline-heading">
                <div><h3>Tracking timeline</h3></div>
              </div>
              <div className="tracking-timeline">
                {selected.DETAILS?.length ? selected.DETAILS.map((item) => (
                  <article className="tracking-event" key={item.ID}>
                    <div className="tracking-dot" aria-hidden="true" />
                    <div>
                          <time>{displayDate(item.TRACKING_DATE)}</time>
                          {item.MODE_OF_SHIPMENT && <small>Mode: {item.MODE_OF_SHIPMENT}</small>}
                          <p>{item.TRACKING_DETAILS}</p>
                      <small>Created by user {item.CREATED_BY}</small>
                    </div>
                    <Button
                      variant="link"
                      aria-label={`Edit tracking entry from ${displayDate(item.TRACKING_DATE)}`}
                      onClick={() => startDetailEdit(item)}
                    >
                      <PencilSquare aria-hidden="true" /> Edit
                    </Button>
                  </article>
                )) : <div className="tracking-empty">No tracking entries yet.</div>}
              </div>
              <DetailForm
                value={detail}
                setValue={setDetail}
                editing={Boolean(editingDetailId)}
                saving={saving}
                onSubmit={saveDetail}
                onCancel={() => {
                  setDetail({ ...emptyDetail, DELIVERY_STATUS: selected.DELIVERY_STATUS || 'Pending' });
                  setEditingDetailId(null);
                }}
              />
            </>
          ) : (
            <div className="tracking-empty tracking-empty-large">
              Select an order to view its timeline, or create a new order.
            </div>
          )}
        </section>
          </div>
        </div>
      </div>
    </main>
  );
}

function HeaderForm({ value, setValue, isNew, saving, onSubmit, onCancel }) {
  const field = (name) => ({
    value: value[name],
    onChange: (e) => setValue({ ...value, [name]: e.target.value })
  });
  return (
    <Form onSubmit={onSubmit} className="tracking-form">
      <div className="tracking-section-heading">
        <div><p className="tracking-eyebrow">{isNew ? 'Create' : 'Update'}</p><h2>Order details</h2></div>
      </div>
      <div className="tracking-form-sections">
        <section className="tracking-form-section" aria-labelledby="order-identity-heading">
          <h3 id="order-identity-heading" className="tracking-form-section-title">Order identity</h3>
          <div className="tracking-form-grid tracking-form-grid-identity">
            <Form.Group>
              <Form.Label>Sales Order Number *</Form.Label>
              <Form.Control maxLength={50} required autoFocus {...field('SALES_ORDER_NO')} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Customer Order Reference</Form.Label>
              <Form.Control maxLength={100} {...field('CUSTOMER_ORDER_REFERENCE')} />
            </Form.Group>
          </div>
        </section>

        <section className="tracking-form-section" aria-labelledby="company-dates-heading">
          <h3 id="company-dates-heading" className="tracking-form-section-title">Company and delivery dates</h3>
          <div className="tracking-form-grid tracking-form-grid-dates">
            <Form.Group>
              <Form.Label>Entity *</Form.Label>
              <Form.Select required {...field('ENTITY')}>
                {entities.map((item) => <option key={item}>{item}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Planned Delivery Date *</Form.Label>
              <Form.Control type="date" required disabled={!isNew} {...field('PLANNED_DELIVERY_DATE')} />
              {!isNew && <Form.Text>Planned delivery date cannot be updated after order creation.</Form.Text>}
            </Form.Group>
            {!isNew && (
              <Form.Group>
                <Form.Label>Current Delivery Date *</Form.Label>
                <Form.Control type="date" required {...field('CURRENT_DELIVERY_DATE')} />
              </Form.Group>
            )}
          </div>
        </section>

        {!isNew && (
          <section className="tracking-form-section tracking-form-section-note" aria-label="Status update location">
            <strong>Delivery status is updated from tracking entries.</strong>
            <span>Use the tracking detail form below to move this order to Pending, In Transit, or Delivered.</span>
          </section>
        )}
      </div>
      <div className="tracking-actions">
        <Button variant="outline-secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button className="tracking-primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create order' : 'Save changes'}
        </Button>
      </div>
    </Form>
  );
}

// eslint-disable-next-line no-unused-vars
function DetailFormLegacy({ value, setValue, editing, saving, onSubmit, onCancel }) {
  return (
    <Form onSubmit={onSubmit} className="tracking-entry-form">
      <h3>{editing ? 'Edit tracking entry' : 'Add tracking entry'}</h3>
      <div className="tracking-entry-grid">
        <Form.Group>
          <Form.Label>Tracking Details *</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            required
            maxLength={1000}
            value={value.TRACKING_DETAILS}
            onChange={(e) => setValue({ ...value, TRACKING_DETAILS: e.target.value })}
          />
          <Form.Text>{value.TRACKING_DETAILS.length}/1000 characters</Form.Text>
        </Form.Group>
        <div className="tracking-entry-side">
          <Form.Group>
            <Form.Label>Mode</Form.Label>
            <Form.Select
              value={value.MODE_OF_SHIPMENT}
              onChange={(e) => setValue({ ...value, MODE_OF_SHIPMENT: e.target.value })}
            >
              <option value="">Select mode</option>
              {shipmentModes.map((item) => <option key={item}>{item}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Delivery Status *</Form.Label>
            <Form.Select
              required
              value={value.DELIVERY_STATUS}
              onChange={(e) => setValue({ ...value, DELIVERY_STATUS: e.target.value })}
            >
              {statuses.map((item) => <option key={item}>{item}</option>)}
            </Form.Select>
          </Form.Group>
          <div className="tracking-entry-actions">
            {editing && <Button variant="outline-secondary" type="button" onClick={onCancel}>Cancel</Button>}
            <Button className="tracking-primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save entry' : 'Add entry'}
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
}

function DetailForm({ value, setValue, editing, saving, onSubmit, onCancel }) {
  return (
    <Form onSubmit={onSubmit} className="tracking-entry-form">
      <div className="tracking-entry-heading">
        <h3>{editing ? 'Edit tracking entry' : 'Add tracking entry'}</h3>
      </div>
      <div className="tracking-entry-grid">
        <section className="tracking-entry-main" aria-label="Tracking update">
          <Form.Group>
            <Form.Label>Tracking Details *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              required
              maxLength={1000}
              value={value.TRACKING_DETAILS}
              onChange={(e) => setValue({ ...value, TRACKING_DETAILS: e.target.value })}
            />
            <Form.Text>{value.TRACKING_DETAILS.length}/1000 characters</Form.Text>
          </Form.Group>
        </section>
        <section className="tracking-entry-side" aria-label="Tracking metadata">
          <Form.Group>
            <Form.Label>Delivery Status *</Form.Label>
            <Form.Select
              required
              value={value.DELIVERY_STATUS}
              onChange={(e) => setValue({ ...value, DELIVERY_STATUS: e.target.value })}
            >
              {statuses.map((item) => <option key={item}>{item}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Mode of Shipment</Form.Label>
            <Form.Select
              value={value.MODE_OF_SHIPMENT}
              onChange={(e) => setValue({ ...value, MODE_OF_SHIPMENT: e.target.value })}
            >
              <option value="">Select mode</option>
              {shipmentModes.map((item) => <option key={item}>{item}</option>)}
            </Form.Select>
          </Form.Group>
          <div className="tracking-entry-actions">
            {editing && <Button variant="outline-secondary" type="button" onClick={onCancel}>Cancel</Button>}
            <Button className="tracking-primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Save entry' : 'Add entry'}
            </Button>
          </div>
        </section>
      </div>
    </Form>
  );
}

export default OrderTrackingPage;
